"use client";

import { cn } from "@guru/shared/utils/cn";
import { TrendingDown, TrendingUp } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { BrowserFrame } from "@/components/marketing/browser-frame";
import { PERSONA_CONTENT } from "@/components/marketing/persona/persona-data";
import { usePersonaStore } from "@/components/marketing/persona/persona-store";
import { ChatBubble, type TickState } from "@/components/marketing/whatsapp-bits";
import { formatINR } from "@/lib/marketing/format";

/**
 * The hero is the product: a fee dashboard owning the frame, with WhatsApp
 * as a side panel — the channel, not the show.
 *
 * Loop steps:
 * 0 — 9pm reminder lands in the WhatsApp panel (ticks: sent)
 * 1 — delivered
 * 2 — read
 * 3 — payment received: confirmation bubble, invoice row flips Paid,
 *     collected/outstanding/rate tick over on the dashboard
 * 4 — hold on the resolved state, then loop
 */
const STEP_DURATIONS_MS = [1200, 900, 1400, 3600, 3600];
const FINAL_STEP = 3;

const RATE_BEFORE = 89;
const RATE_AFTER = 92;

// Entrance choreography: frame rises, rows cascade in, the WhatsApp panel
// slides in last — the dashboard assembles itself before the loop begins.
const rowsContainerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.5 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -16 },
  show: {
    opacity: 1,
    x: 0,
    transition: { type: "spring" as const, stiffness: 260, damping: 24 },
  },
};

export function HeroDiorama({ className }: { className?: string }) {
  const prefersReducedMotion = useReducedMotion();
  const persona = usePersonaStore((state) => state.persona);
  const content = PERSONA_CONTENT[persona];

  const [step, setStep] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion || paused) return;
    const timer = setTimeout(() => {
      const next = (step + 1) % STEP_DURATIONS_MS.length;
      if (next === 0) setCycle((count) => count + 1);
      setStep(next);
    }, STEP_DURATIONS_MS[step]);
    return () => clearTimeout(timer);
  }, [step, paused, prefersReducedMotion]);

  // Reduced motion gets the resolved frame: the whole story, no movement.
  const effectiveStep = prefersReducedMotion ? FINAL_STEP : step;

  const ticks: TickState = effectiveStep >= 2 ? "read" : effectiveStep >= 1 ? "delivered" : "sent";
  const isPaid = effectiveStep >= FINAL_STEP;

  const fee = content.monthlyFee;
  const feeLabel = formatINR(fee);
  const collected = formatINR(fee * 75 + (isPaid ? fee : 0));
  const outstanding = formatINR(fee * (isPaid ? 8 : 9));
  const rate = isPaid ? RATE_AFTER : RATE_BEFORE;

  // Hero row first — the WhatsApp panel overlaps the bottom rows, and the
  // badge flip is the moment the whole loop builds to.
  const rows = [
    { name: content.studentName, batch: content.batchName, hero: true },
    { name: "Meera J.", batch: content.secondBatchName, hero: false },
    { name: "Arjun T.", batch: content.batchName, hero: false },
  ];

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: tap-to-replay is a redundant affordance — the animation loops on its own, so keyboard users miss nothing
    <div
      role="img"
      aria-label={`Animation of the EtheraGuru fee dashboard: at 9pm an automatic WhatsApp reminder goes to ${content.studentName}'s parent, the payment arrives by the link, the invoice marks itself paid and the collection rate ticks up — while the owner is still teaching.`}
      className={cn("relative mx-auto w-full max-w-xl select-none sm:pb-16", className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => setStep(0)}
    >
      <div aria-hidden="true">
        {/* Narrative time chip */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.4 }}
          className="absolute -top-3 left-3 z-30 rounded-full border border-border bg-background/90 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur"
        >
          21:02 — while you were teaching
        </motion.div>

        {/* The product, front and centre — assembles itself on arrival */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 170, damping: 22 }}
        >
          <BrowserFrame className="shadow-2xl">
            <div className="p-4 sm:p-5">
              {/* Dashboard header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">Fees — July</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Invoice day ran on the 1st · 42 invoices · 0 typed by hand
                  </p>
                </div>
                <div className="shrink-0 rounded-lg bg-brand/10 px-3 py-1.5 text-right">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-brand-deep/80">
                    Collection rate
                  </p>
                  <div className="flex items-center justify-end gap-1.5">
                    <motion.p
                      key={rate}
                      initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-base font-bold tabular-nums text-brand-deep"
                    >
                      {rate}%
                    </motion.p>
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-positive/10 px-1 py-0.5 text-[9px] font-semibold text-positive">
                      <TrendingUp className="h-2.5 w-2.5" aria-hidden="true" />
                      {isPaid ? "+3" : "+1"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-brand/30 bg-brand/5 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Collected this month
                  </p>
                  <motion.p
                    key={collected}
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-0.5 text-lg font-bold tabular-nums text-brand-deep"
                  >
                    {collected}
                  </motion.p>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-positive/10 px-1.5 py-0.5 text-[10px] font-semibold text-positive">
                    <TrendingUp className="h-2.5 w-2.5" aria-hidden="true" />
                    +18% vs last month
                  </span>
                </div>
                <div className="rounded-lg border border-border bg-background p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Outstanding
                  </p>
                  <motion.p
                    key={outstanding}
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-0.5 text-lg font-bold tabular-nums text-foreground"
                  >
                    {outstanding}
                  </motion.p>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-positive/10 px-1.5 py-0.5 text-[10px] font-semibold text-positive">
                    <TrendingDown className="h-2.5 w-2.5" aria-hidden="true" />
                    {isPaid ? "−12% · good" : "−4% · good"}
                  </span>
                </div>
              </div>

              {/* Six-month trend — draws itself in on arrival */}
              <div className="mt-2 rounded-lg border border-border bg-background px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Collections · last 6 months
                  </p>
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-positive">
                    <TrendingUp className="h-3 w-3" aria-hidden="true" />
                    trending up
                  </span>
                </div>
                <svg
                  viewBox="0 0 100 26"
                  preserveAspectRatio="none"
                  className="mt-1.5 h-8 w-full"
                  aria-hidden="true"
                >
                  <motion.path
                    d="M2 23 L20 19.5 L37 20.5 L54 14 L71 11.5 L86 8.5 L98 4.5"
                    fill="none"
                    stroke="var(--brand-navy)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    initial={prefersReducedMotion ? false : { pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1.1, duration: 1.2, ease: "easeOut" }}
                  />
                  <motion.circle
                    cx="98"
                    cy="4.5"
                    fill="var(--brand-saffron)"
                    initial={prefersReducedMotion ? false : { r: 0, opacity: 0 }}
                    animate={{ r: 2.4, opacity: 1 }}
                    transition={{ delay: 2.2, type: "spring", stiffness: 260, damping: 16 }}
                  />
                </svg>
              </div>

              {/* Invoice rows */}
              <motion.div
                className="mt-4 space-y-1.5"
                variants={prefersReducedMotion ? undefined : rowsContainerVariants}
                initial={prefersReducedMotion ? false : "hidden"}
                animate="show"
              >
                {rows.map((row) => {
                  const rowPaid = row.hero ? isPaid : true;
                  return (
                    <motion.div
                      key={row.name}
                      variants={prefersReducedMotion ? undefined : rowVariants}
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-lg border px-3 py-2",
                        row.hero ? "border-brand/30 bg-brand/[0.04]" : "border-border bg-background"
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
                          {row.name.charAt(0)}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] font-medium text-foreground">
                            {row.name}
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {row.batch}
                          </span>
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-[13px] font-semibold tabular-nums text-foreground">
                          {feeLabel}
                        </span>
                        {row.hero ? (
                          <AnimatePresence mode="wait" initial={false}>
                            {rowPaid ? (
                              <motion.span
                                key="paid"
                                initial={
                                  prefersReducedMotion
                                    ? false
                                    : { scale: 0.5, rotate: -8, opacity: 0 }
                                }
                                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0, transition: { duration: 0.12 } }}
                                transition={{ type: "spring", stiffness: 320, damping: 18 }}
                                className="rounded-full bg-positive/15 px-2 py-0.5 text-[11px] font-bold text-positive"
                              >
                                Paid
                              </motion.span>
                            ) : (
                              <motion.span
                                key="unpaid"
                                initial={false}
                                exit={{ scale: 0.8, opacity: 0, transition: { duration: 0.12 } }}
                                className="rounded-full bg-due/15 px-2 py-0.5 text-[11px] font-bold text-due"
                              >
                                Unpaid
                              </motion.span>
                            )}
                          </AnimatePresence>
                        ) : (
                          <span className="rounded-full bg-positive/10 px-2 py-0.5 text-[11px] font-semibold text-positive/80">
                            Paid
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </BrowserFrame>
        </motion.div>

        {/* WhatsApp side panel — the channel, slides in once the dashboard lands */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, x: 40, rotate: 4 }}
          animate={{ opacity: 1, x: 0, rotate: 1 }}
          transition={{ delay: 0.9, type: "spring", stiffness: 180, damping: 20 }}
          className="relative z-20 -mt-5 ml-auto w-[88%] max-w-[300px] overflow-hidden rounded-xl border border-border shadow-2xl sm:absolute sm:-bottom-12 sm:-right-2 sm:mt-0 sm:w-72 lg:-right-6"
        >
          <div className="flex items-center gap-2 bg-[#008069] px-3 py-2 text-white dark:bg-[#202c33]">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/25 text-[10px] font-bold">
              {content.orgName.charAt(0)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-xs font-semibold leading-tight">
                {content.orgName}
              </span>
              <span className="block text-[10px] leading-tight text-white/80">
                WhatsApp Business
              </span>
            </span>
          </div>
          <div className="space-y-2 bg-[#efeae2] p-3 dark:bg-[#0b141a]">
            {/* Reminder template — re-enters each loop */}
            <motion.div
              key={`reminder-${cycle}-${persona}`}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <ChatBubble direction="out" time="9:02 pm" ticks={ticks}>
                <span className="block font-semibold">Fee reminder</span>
                <span className="mt-0.5 block">
                  {content.studentName} — <strong>{feeLabel}</strong> due Friday
                </span>
                <span
                  className={cn(
                    "mt-1.5 block rounded-md border border-[#00806933] bg-white/70 px-2 py-1 text-center text-[12px] font-semibold text-[#008069]",
                    "dark:border-[#00a884]/40 dark:bg-white/10 dark:text-[#00a884]",
                    effectiveStep >= 2 && !prefersReducedMotion && "animate-pulse"
                  )}
                >
                  🔗 Pay securely
                </span>
              </ChatBubble>
            </motion.div>

            {/* Payment confirmation */}
            <AnimatePresence>
              {isPaid && (
                <motion.div
                  key={`paid-${persona}`}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 14, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  transition={{ type: "spring", stiffness: 260, damping: 24 }}
                >
                  <ChatBubble direction="out" time="9:04 pm" ticks="delivered">
                    <span className="block font-semibold text-[#008069] dark:text-[#00a884]">
                      ✅ Payment received — {feeLabel}
                    </span>
                    <span className="mt-0.5 block">Receipt for INV-0042 is on its way.</span>
                  </ChatBubble>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="pt-0.5 text-center text-[10px] text-[#667781] dark:text-[#8696a0]">
              Sent automatically. Nobody typed a thing.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
